import React, { useMemo, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { cn } from '@/lib/utils';
import IconRecentFollow from '@/icons/navbar/icon-recent-follow.svg';
import IconUnfollow from '@/icons/navbar/icon-unfollow.svg';
import IconComments from '@/icons/navbar/icon-comments.svg';
import IconLikes from '@/icons/navbar/icon-likes.svg';
import IconStory from '@/icons/navbar/icon-story.svg';
import IconExport from '@/icons/navbar/icon-export.svg';

interface NavItem {
  key: string;
  icon: string;
  title: string;
  path: string;
}

const ToolkitsNavBar: React.FC = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const navContainerRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLAnchorElement>(null);

  const navItems: NavItem[] = useMemo(
    () => [
      {
        key: 'recent-follower',
        icon: IconRecentFollow,
        title: t('Recent Follow'),
        path: '/instagram-new-follow-tracker',
      },
      {
        key: 'unfollow',
        icon: IconUnfollow,
        title: t('Unfollow'),
        path: '/instagram-unfollowers-tracker',
      },
      {
        key: 'follower-export',
        icon: IconExport,
        title: t('Follower Export'),
        path: '/ig-follower-export-tool',
      },
      {
        key: 'post-comments',
        icon: IconComments,
        title: t('Post Comments'),
        path: '/instagram-viewer/post-comments',
      },
      {
        key: 'post-likes',
        icon: IconLikes,
        title: t('Post Likes'),
        path: '/instagram-viewer/post-likes',
      },
      {
        key: 'story',
        icon: IconStory,
        title: t('Story'),
        path: '/anonymous-instagram-story-viewer',
      },
    ],
    [t]
  );

  // 检查当前路由是否在导航项中
  const isRouteActive = (path: string): boolean => {
    // 获取不带查询参数的路径
    const currentPath = router.asPath.split('?')[0];
    return router.pathname === path || currentPath === path;
  };

  // 滚动活动项到视口中心
  useEffect(() => {
    if (navContainerRef.current && activeItemRef.current) {
      // 使用requestAnimationFrame确保DOM已完全渲染
      requestAnimationFrame(() => {
        const container = navContainerRef.current;
        const activeItem = activeItemRef.current;

        if (!container || !activeItem) return;

        // 检查活动项是否在视口中
        const containerRect = container.getBoundingClientRect();
        const activeItemRect = activeItem.getBoundingClientRect();

        // 计算活动项相对于容器的位置
        const isVisible = activeItemRect.left >= containerRect.left && activeItemRect.right <= containerRect.right;

        if (!isVisible) {
          // 计算滚动位置，使活动项居中
          const scrollLeft =
            activeItemRect.left +
            container.scrollLeft -
            containerRect.left -
            (containerRect.width - activeItemRect.width) / 2;

          // 平滑滚动到计算的位置
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      });
    }
  }, [router.asPath]); // 路由变化时重新执行

  return (
    <div className="w-full bg-white md:pt-8">
      <div
        ref={navContainerRef}
        className={cn(
          'max-w-screen-xl mx-auto text-center',
          'overflow-x-auto no-scrollbar md:snap-x md:snap-mandatory'
        )}
      >
        <div className={cn('inline-flex', 'justify-start md:justify-center items-center rounded-lg bg-[#ECEDF4]')}>
          {navItems.map((item) => {
            const isActive = isRouteActive(item.path);
            return (
              <Link
                href={item.path}
                key={item.key}
                className="block flex-shrink-0 md:snap-start md:snap-always"
                ref={isActive ? activeItemRef : null}
              >
                <div
                  className={cn(
                    'flex items-center px-4 py-2.5 rounded-lg hover:bg-[#DDE0F5] transition-colors active:opacity-70',
                    {
                      'text-[var(--app-primary-color)] font-medium relative bg-[#DDE0F5]': isActive,
                      'text-gray-700': !isActive,
                    }
                  )}
                >
                  <div className="flex items-center justify-center">
                    <span
                      className={cn('text-[20px]', {
                        'text-[var(--app-primary-color)]': isActive,
                      })}
                    >
                      <item.icon />
                    </span>
                  </div>
                  <span className="whitespace-nowrap ml-1 text-[16px] leading-[22px]">{item.title}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ToolkitsNavBar;
